import React, { useState, useEffect, useRef } from 'react';
import {
  Bell, BellOff, CheckCircle, X, RefreshCw,
  FileText, ShoppingCart, GripVertical, Loader2,
  AlertCircle, ClipboardList, RotateCcw, Fuel, PackageCheck, SlidersHorizontal
} from 'lucide-react';
import API_BASE_URL from "../config";

export default function Notification() {
  const [panelOpen, setPanelOpen]                       = useState(false);
  const [notifications, setNotifications]               = useState([]);
  const [seenNotifications, setSeenNotifications]       = useState([]);
  const [loading, setLoading]                           = useState(false);
  const [refreshing, setRefreshing]                     = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal]                       = useState(false);
  const [currentUser, setCurrentUser]                   = useState(null);
  const [notifConfig, setNotifConfig]                   = useState(null);
  const [activeTab, setActiveTab]                       = useState('unread');
  const [notifSettings, setNotifSettings]               = useState(null);
  const [noticeAllowed, setNoticeAllowed]               = useState(true);

  const [position, setPosition]       = useState({ x: window.innerWidth - 420, y: 0 });
  const [width, setWidth]             = useState(400);
  const [isDragging, setIsDragging]   = useState(false);
  const [isResizing, setIsResizing]   = useState(false);
  const [resizeSide, setResizeSide]   = useState(null);
  const [dragStart, setDragStart]     = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, width: 0, posX: 0 });
  const panelRef = useRef(null);

  const [iconPosition, setIconPosition]     = useState({ x: window.innerWidth - 80, y: 20 });
  const [isDraggingIcon, setIsDraggingIcon] = useState(false);
  const [iconDragStart, setIconDragStart]   = useState({ x: 0, y: 0 });
  const iconRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try { setCurrentUser(JSON.parse(userData)); }
      catch (e) { console.error('Error parsing user:', e); }
    }
  }, []);

  useEffect(() => { if (currentUser) fetchConfigThenNotifications(); }, [currentUser]);
  useEffect(() => { if (panelOpen && currentUser) fetchConfigThenNotifications(); }, [panelOpen]);

  const isEnabled = (val) =>
    val === true || val === 1 || val === '1' || String(val).toLowerCase() === 'true';

  const getFRConfigEnabled = (config) => {
    if (!config) return false;
    const val = config.fuelRequisition ?? config.fuel_requisition ?? config.fuelrequisition ??
      config.FuelRequisition ?? config.Fuel_Requisition ?? config.fuel ?? config.Fuel ??
      config.fr ?? config.FR ?? null;
    return isEnabled(val);
  };

  const getGRConfigEnabled = (config) => {
    if (!config) return false;
    const val = config.gr ?? config.GR ?? config.goodsReceipt ?? config.goods_receipt ??
      config.GoodsReceipt ?? config.goodsreciept ?? null;
    return isEnabled(val);
  };

  const getSAConfigEnabled = (config) => {
    if (!config) return false;
    const val = config.stockAdjustment ?? config.stock_adjustment ?? config.StockAdjustment ??
      config.sa ?? config.SA ?? config.adjustment ?? config.Adjustment ?? null;
    return isEnabled(val);
  };

  const fetchConfigThenNotifications = async () => {
    if (!currentUser?.userId) return;
    setLoading(true);
    try {
      // ── Check NotificationSettings.notice_allowed first ──
      try {
        const nsRes  = await fetch(`${API_BASE_URL}api/NotificationSettings/user/${currentUser.userId}`);
        const nsData = await nsRes.json();
        setNotifSettings(nsData);
        const allowed = nsData?.notice_allowed;
        const isAllowed = allowed === true || allowed === 1 || String(allowed).toLowerCase() === 'true';
        setNoticeAllowed(isAllowed);
        if (!isAllowed) {
          setNotifications([]); setSeenNotifications([]); setLoading(false); return;
        }
      } catch (e) {
        console.warn('Could not fetch NotificationSettings, proceeding anyway.', e);
        setNoticeAllowed(true);
      }

      const configRes  = await fetch(`${API_BASE_URL}api/Configuration_Notice`);
      const configData = await configRes.json();
      const configs    = Array.isArray(configData) ? configData : [configData];

      const possiblePositions = [
        currentUser.positionId, currentUser.position, currentUser.userLevel,
        currentUser.userLevelName, currentUser.role, currentUser.roleName,
        currentUser.positionName, currentUser.jobTitle, currentUser.level, currentUser.userPosition,
      ].filter(Boolean);

      const config = configs.find(c =>
        possiblePositions.some(p => c.position?.toLowerCase().trim() === p?.toLowerCase().trim())
      );

      setNotifConfig(config || null);

      if (!config || !isEnabled(config.allowed_Notification)) {
        setNotifications([]); setSeenNotifications([]); setLoading(false); return;
      }
      if (!isEnabled(config.ownNotice) && !isEnabled(config.allNotice)) {
        setNotifications([]); setSeenNotifications([]); setLoading(false); return;
      }

      const uid       = currentUser.userId;
      const frEnabled = getFRConfigEnabled(config);
      const grEnabled = getGRConfigEnabled(config);
      const saEnabled = getSAConfigEnabled(config);

      let prNotifRaw = [], prSeenRaw = [];
      let poNotifRaw = [], poSeenRaw = [];
      let mrNotifRaw = [], mrSeenRaw = [];
      let rrNotifRaw = [], rrSeenRaw = [];
      let frNotifRaw = [], frSeenRaw = [];
      let grNotifRaw = [], grSeenRaw = [];
      let saNotifRaw = [], saSeenRaw = [];

      await Promise.all([
        isEnabled(config.pr) && fetch(`${API_BASE_URL}api/PR_Header/notifications`)
          .then(r => r.json()).then(d => { prNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.pr) && fetch(`${API_BASE_URL}api/PR_Header/seen`)
          .then(r => r.json()).then(d => { prSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.po) && fetch(`${API_BASE_URL}api/T_PO_Header/notification`)
          .then(r => r.json()).then(d => { poNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.po) && fetch(`${API_BASE_URL}api/T_PO_Header/seen`)
          .then(r => r.json()).then(d => { poSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.itemRequest) && fetch(`${API_BASE_URL}api/MRHeader/notifications`)
          .then(r => r.json()).then(d => { mrNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.itemRequest) && fetch(`${API_BASE_URL}api/MRHeader/seen`)
          .then(r => r.json()).then(d => { mrSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.itemRequest) && fetch(`${API_BASE_URL}api/ReturnRequest/notifications`)
          .then(r => r.json()).then(d => { rrNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        isEnabled(config.itemRequest) && fetch(`${API_BASE_URL}api/ReturnRequest/seen`)
          .then(r => r.json()).then(d => { rrSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        frEnabled && fetch(`${API_BASE_URL}api/FuelRequisition/notifications`)
          .then(r => r.json()).then(d => { frNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        frEnabled && fetch(`${API_BASE_URL}api/FuelRequisition/seen`)
          .then(r => r.json()).then(d => { frSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        grEnabled && fetch(`${API_BASE_URL}api/GR_Header/notifications`)
          .then(r => r.json()).then(d => { grNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        grEnabled && fetch(`${API_BASE_URL}api/GR_Header/seen`)
          .then(r => r.json()).then(d => { grSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        saEnabled && fetch(`${API_BASE_URL}api/StockAdjustment/notifications`)
          .then(r => r.json()).then(d => { saNotifRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
        saEnabled && fetch(`${API_BASE_URL}api/StockAdjustment/seen`)
          .then(r => r.json()).then(d => { saSeenRaw = Array.isArray(d) ? d : []; }).catch(() => {}),
      ].filter(Boolean));

      const filterByScope = (items, type) => {
        if (!Array.isArray(items)) return [];
        const tagged = items.map(i => ({ ...i, type }));
        if (isEnabled(config.allNotice)) return tagged;
        if (isEnabled(config.ownNotice)) return tagged.filter(i =>
          i.createdBy === uid || i.createdBy === String(uid) ||
          i.requestedBy === uid || i.requestedBy === String(uid) ||
          i.adjustedBy === uid || i.adjustedBy === String(uid)
        );
        return [];
      };

      const newNotifs = [
        ...(isEnabled(config.pr)          ? filterByScope(prNotifRaw, 'PR') : []),
        ...(isEnabled(config.po)          ? filterByScope(poNotifRaw, 'PO') : []),
        ...(isEnabled(config.itemRequest) ? filterByScope(mrNotifRaw, 'IR') : []),
        ...(isEnabled(config.itemRequest) ? filterByScope(rrNotifRaw, 'RR') : []),
        ...(frEnabled                     ? filterByScope(frNotifRaw, 'FR') : []),
        ...(grEnabled                     ? filterByScope(grNotifRaw, 'GR') : []),
        ...(saEnabled                     ? filterByScope(saNotifRaw, 'SA') : []),
      ];

      const newSeen = [
        ...(isEnabled(config.pr)          ? filterByScope(prSeenRaw,  'PR') : []),
        ...(isEnabled(config.po)          ? filterByScope(poSeenRaw,  'PO') : []),
        ...(isEnabled(config.itemRequest) ? filterByScope(mrSeenRaw,  'IR') : []),
        ...(isEnabled(config.itemRequest) ? filterByScope(rrSeenRaw,  'RR') : []),
        ...(frEnabled                     ? filterByScope(frSeenRaw,  'FR') : []),
        ...(grEnabled                     ? filterByScope(grSeenRaw,  'GR') : []),
        ...(saEnabled                     ? filterByScope(saSeenRaw,  'SA') : []),
      ];

      setNotifications(newNotifs);
      setSeenNotifications(newSeen);

    } catch (err) {
      console.error('fetchConfigThenNotifications crashed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConfigThenNotifications();
    setTimeout(() => setRefreshing(false), 600);
  };

  const closeModal = () => { setShowModal(false); setSelectedNotification(null); };

  const handleNotificationClick = async (notif, isFromSeenList) => {
    setSelectedNotification(notif);
    setShowModal(true);
    if (isFromSeenList) return;
    try {
      const endpoint =
        notif.type === 'PR' ? 'PR_Header'
        : notif.type === 'PO' ? 'T_PO_Header'
        : notif.type === 'RR' ? 'ReturnRequest'
        : notif.type === 'FR' ? 'FuelRequisition'
        : notif.type === 'GR' ? 'GR_Header'
        : notif.type === 'SA' ? 'StockAdjustment'
        : 'MRHeader';
      const res = await fetch(`${API_BASE_URL}api/${endpoint}/mark-notification/${notif.id}`, { method: 'PATCH' });
      if (!res.ok) await fetch(`${API_BASE_URL}api/${endpoint}/mark-notification/${notif.id}`, { method: 'POST' });
      setNotifications(prev => prev.filter(n => !(n.id === notif.id && n.type === notif.type)));
      setSeenNotifications(prev => [{ ...notif, notification: true }, ...prev]);
    } catch (err) {
      console.error('Failed to mark notification:', err);
    }
  };

  // Icon drag
  const handleIconMouseDown = (e) => {
    setIsDraggingIcon(true);
    setIconDragStart({ x: e.clientX - iconPosition.x, y: e.clientY - iconPosition.y });
    e.stopPropagation();
  };
  useEffect(() => {
    if (!isDraggingIcon) return;
    const move = (e) => setIconPosition({
      x: Math.max(10, Math.min(window.innerWidth - 70, e.clientX - iconDragStart.x)),
      y: Math.max(10, Math.min(window.innerHeight - 70, e.clientY - iconDragStart.y)),
    });
    const up = () => setIsDraggingIcon(false);
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [isDraggingIcon, iconDragStart]);

  // Panel drag/resize
  const handleMouseDown = (e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('button')) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  useEffect(() => {
    if (!isDragging && !isResizing) return;
    const move = (e) => {
      if (isDragging) setPosition({
        x: Math.max(0, Math.min(window.innerWidth - width, e.clientX - dragStart.x)),
        y: Math.max(0, Math.min(window.innerHeight - 600, e.clientY - dragStart.y)),
      });
      if (isResizing && resizeSide === 'left') {
        const newW = Math.max(360, Math.min(800, resizeStart.width - (e.clientX - resizeStart.x)));
        setWidth(newW);
        setPosition(prev => ({ ...prev, x: resizeStart.posX + (resizeStart.width - newW) }));
      }
      if (isResizing && resizeSide === 'right')
        setWidth(Math.max(360, Math.min(800, e.clientX - position.x)));
    };
    const up = () => { setIsDragging(false); setIsResizing(false); setResizeSide(null); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [isDragging, isResizing, dragStart, resizeSide, resizeStart, position, width]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const diffMs = Date.now() - date;
    const m = Math.floor(diffMs / 60000);
    const h = Math.floor(diffMs / 3600000);
    const d = Math.floor(diffMs / 86400000);
    if (m < 1)  return 'Just now';
    if (m < 60) return `${m}m ago`;
    if (h < 24) return `${h}h ago`;
    if (d < 7)  return `${d}d ago`;
    return date.toLocaleDateString();
  };

  const getNotifDate = (notif) => {
    if (notif.type === 'PR') return notif.creationDate;
    if (notif.type === 'PO') return notif.poCreatedDate;
    if (notif.type === 'FR') return notif.dateCreated;
    if (notif.type === 'GR') return notif.createdAt;
    if (notif.type === 'SA') return notif.adjustmentDate || notif.createdDate;
    return notif.createdDate;
  };

  const getNotifTitle = (notif) => {
    if (notif.type === 'PR') return notif.prNo;
    if (notif.type === 'PO') return notif.poNumber;
    if (notif.type === 'RR') return notif.itemCode;
    if (notif.type === 'FR') return notif.controlNo || `FR-${notif.id}`;
    if (notif.type === 'GR') return notif.grNo || `GR-${notif.id}`;
    if (notif.type === 'SA') return notif.adjustmentNo || `SA-${notif.id}`;
    return notif.mrNo;
  };

  const getNotifSubtitle = (notif) => {
    if (notif.type === 'RR') return notif.returnReason || 'No reason';
    if (notif.type === 'FR') return notif.purpose || notif.description || notif.requestorName || 'No description';
    if (notif.type === 'GR') return notif.vendorName || notif.poNumber || 'No vendor info';
    if (notif.type === 'SA') return `${notif.adjustmentType || 'Adjustment'} · ${notif.itemCode || 'N/A'} · ${notif.warehouseCode || ''}`;
    return notif.remarks || 'No remarks';
  };

  const STATUS_MAP   = { 0: 'Draft', 1: 'Pending', 2: 'Approved', 3: 'Rejected', 4: 'Cancelled' };
  const STATUS_STYLE = {
    0: { bg: '#f1f5f9', color: '#64748b' },
    1: { bg: '#fef9c3', color: '#a16207' },
    2: { bg: '#f0fdf4', color: '#16a34a' },
    3: { bg: '#fef2f2', color: '#dc2626' },
    4: { bg: '#f1f5f9', color: '#64748b' },
  };
  const RR_STATUS_MAP   = { 0: 'Pending', 1: 'Approved', '-1': 'Rejected' };
  const RR_STATUS_STYLE = {
    0:    { bg: '#fef9c3', color: '#a16207' },
    1:    { bg: '#f0fdf4', color: '#16a34a' },
    '-1': { bg: '#fef2f2', color: '#dc2626' },
  };
  const GR_STATUS_MAP   = { 1: 'Active', 3: 'Closed', '-1': 'Deleted', '-2': 'Reverted' };
  const GR_STATUS_STYLE = {
    1:    { bg: '#fef9c3', color: '#a16207' },
    3:    { bg: '#f0fdf4', color: '#16a34a' },
    '-1': { bg: '#f1f5f9', color: '#64748b' },
    '-2': { bg: '#fef2f2', color: '#dc2626' },
  };
  const SA_STATUS_MAP   = { 1: 'Active', 0: 'Cancelled' };
  const SA_STATUS_STYLE = {
    1: { bg: '#f0fdf4', color: '#16a34a' },
    0: { bg: '#f1f5f9', color: '#64748b' },
  };

  const getStatusText = (s, type) => {
    if (type === 'GR') return GR_STATUS_MAP[String(s)] ?? 'Unknown';
    if (type === 'SA') return SA_STATUS_MAP[String(s)] ?? 'Unknown';
    if (['RR', 'IR', 'FR'].includes(type)) return RR_STATUS_MAP[String(s)] ?? 'Unknown';
    return STATUS_MAP[s] ?? 'Unknown';
  };
  const getStatusStyle = (s, type) => {
    if (type === 'GR') return GR_STATUS_STYLE[String(s)] ?? GR_STATUS_STYLE[1];
    if (type === 'SA') return SA_STATUS_STYLE[String(s)] ?? SA_STATUS_STYLE[1];
    if (['RR', 'IR', 'FR'].includes(type)) return RR_STATUS_STYLE[String(s)] ?? RR_STATUS_STYLE[0];
    return STATUS_STYLE[s] ?? STATUS_STYLE[0];
  };

  const getAdjTypeBadge = (adjType) => {
    const map = {
      'increase':       { bg: '#f0fdf4', color: '#16a34a', border: '#dcfce7' },
      'decrease':       { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
      'physical count': { bg: '#eff6ff', color: '#1d4ed8', border: '#dbeafe' },
      'transfer':       { bg: '#f5f3ff', color: '#6d28d9', border: '#ede9fe' },
      'damage':         { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
      'expiry':         { bg: '#fdf4ff', color: '#7e22ce', border: '#f3e8ff' },
    };
    return map[(adjType || '').toLowerCase()] || { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
  };

  const TYPE_CONFIG = {
    PR: { label: 'Purchase Request',  bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe', icon: <FileText size={10} />,         avatar: '📋' },
    PO: { label: 'Purchase Order',    bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', icon: <ShoppingCart size={10} />,     avatar: '🛒' },
    RR: { label: 'Return Request',    bg: '#fef2f2', color: '#dc2626', border: '#fecaca', icon: <RotateCcw size={10} />,        avatar: '↩️' },
    IR: { label: 'Item Request',      bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', icon: <ClipboardList size={10} />,   avatar: '📦' },
    FR: { label: 'Fuel Requisition',  bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', icon: <Fuel size={10} />,            avatar: '⛽' },
    GR: { label: 'Goods Receipt',     bg: '#f0fdfa', color: '#0f766e', border: '#99f6e4', icon: <PackageCheck size={10} />,   avatar: '✅' },
    SA: { label: 'Stock Adjustment',  bg: '#fdf4ff', color: '#7e22ce', border: '#f3e8ff', icon: <SlidersHorizontal size={10} />, avatar: '📊' },
  };

  const totalUnread = notifications.length;
  const notifAllowed = notifConfig
    ? isEnabled(notifConfig.allowed_Notification) && (isEnabled(notifConfig.ownNotice) || isEnabled(notifConfig.allNotice))
    : true;

  const displayList = activeTab === 'unread' ? notifications : seenNotifications;

  // ── FB-style notification row ──
  const NotifRow = ({ notif, isFromSeen }) => {
    const tc = TYPE_CONFIG[notif.type] || TYPE_CONFIG.IR;
    return (
      <div
        onClick={() => handleNotificationClick(notif, isFromSeen)}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          borderRadius: 8,
          margin: '2px 8px',
          background: !isFromSeen ? '#e7f0fd' : 'transparent',
          transition: 'background .15s',
          position: 'relative',
        }}
        onMouseEnter={e => e.currentTarget.style.background = !isFromSeen ? '#d8e8fc' : '#f0f2f5'}
        onMouseLeave={e => e.currentTarget.style.background = !isFromSeen ? '#e7f0fd' : 'transparent'}
      >
        {/* Avatar circle */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: `linear-gradient(135deg, ${tc.color}22, ${tc.color}44)`,
            border: `2px solid ${tc.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>
            {tc.avatar}
          </div>
          {/* Type badge on avatar */}
          <div style={{
            position: 'absolute', bottom: -2, right: -2,
            width: 20, height: 20, borderRadius: '50%',
            background: tc.color,
            border: '2px solid #fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff',
          }}>
            {React.cloneElement(tc.icon, { size: 9 })}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: '0 0 2px', fontSize: 13.5, color: '#050505', lineHeight: 1.4 }}>
            <span style={{ fontWeight: 700 }}>{tc.label}</span>
            {' · '}
            <span style={{ fontWeight: 600 }}>{getNotifTitle(notif)}</span>
          </p>
          <p style={{ margin: '0 0 4px', fontSize: 12.5, color: '#65676b', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
            {getNotifSubtitle(notif)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: !isFromSeen ? '#1877f2' : '#65676b' }}>
              {formatDate(getNotifDate(notif))}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 600,
              padding: '1px 7px', borderRadius: 20,
              background: getStatusStyle(notif.status, notif.type).bg,
              color: getStatusStyle(notif.status, notif.type).color,
            }}>
              {getStatusText(notif.status, notif.type)}
            </span>
          </div>
        </div>

        {/* Unread blue dot */}
        {!isFromSeen && (
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: '#1877f2', flexShrink: 0, marginTop: 6,
          }} />
        )}
      </div>
    );
  };

  // Modal field
  const FieldCard = ({ label, val, full }) => (
    <div style={{
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 9,
      padding: '10px 12px', gridColumn: full ? 'span 2' : 'span 1',
    }}>
      <p style={{ margin: '0 0 3px', fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{String(val ?? 'N/A')}</p>
    </div>
  );

  const getFRModalFields = (n) => [
    ['Control No', n.controlNo || 'N/A', false], ['Requestor', n.requestorName || 'N/A', false],
    ['Plate Number', n.plateNumber || 'N/A', false], ['Department', n.department || 'N/A', false],
    ['Purpose', n.purpose || 'N/A', true], ['Quantity (L)', n.quantityLiters ?? 'N/A', false],
    ['Actual Liters', n.actualLiters ?? 'N/A', false], ['Truck Odometer', n.truckOdometer ?? 'N/A', false],
    ['Flow Meter Beg', n.flowMeterBeg ?? 'N/A', false], ['Flow Meter End', n.flowMeterEnd ?? 'N/A', false],
    ['Requested By', n.requestedBy || 'N/A', false], ['Approved By', n.approvedBy || 'N/A', false],
    ['Noted By', n.notedBy || 'N/A', false], ['GasMan', n.gasManAssistBy || 'N/A', false],
  ];

  const getGRModalFields = (n) => [
    ['GR Number', n.grNo || 'N/A', false], ['PO Number', n.poNumber || 'N/A', false],
    ['Vendor', n.vendorName || 'N/A', false], ['Vendor ID', n.vendorId ?? 'N/A', false],
    ['Received By', n.receivedBy || 'N/A', false],
    ['GR Date', n.grDate ? new Date(n.grDate).toLocaleDateString() : 'N/A', false],
    ['Total Items', n.grDetails?.length ?? 0, false],
    ['Status', GR_STATUS_MAP[String(n.status)] || 'Unknown', false],
    ['Remarks', n.remarks || 'N/A', true],
  ];

  const getSAModalFields = (n) => [
    ['Adjustment No', n.adjustmentNo || 'N/A', false], ['Adjustment Type', n.adjustmentType || 'N/A', false],
    ['Item Code', n.itemCode || 'N/A', false], ['Item Desc', n.itemDescription || 'N/A', true],
    ['Warehouse', n.warehouseCode || 'N/A', false], ['Batch No', n.batchNo || '—', false],
    ['Qty Before', n.quantityBefore ?? 0, false], ['Adj Qty', n.adjustmentQty ?? 0, false],
    ['Qty After', n.quantityAfter ?? 0, false], ['UOM', n.uom || 'pcs', false],
    ['Reason', n.reason || 'N/A', false],
    ['Adjusted By', n.adjustedByName || (n.adjustedBy ? `User ${n.adjustedBy}` : 'N/A'), false],
    ['Reference No', n.referenceNo || '—', false],
    ['Adj Date', n.adjustmentDate ? new Date(n.adjustmentDate).toLocaleDateString() : 'N/A', false],
  ];

  return (
    <>
      <style>{`
        @keyframes bell-swing {
          0%,100% { transform: rotate(0deg); }
          15%      { transform: rotate(18deg); }
          30%      { transform: rotate(-14deg); }
          45%      { transform: rotate(10deg); }
          60%      { transform: rotate(-6deg); }
          75%      { transform: rotate(3deg); }
        }
        @keyframes ripple {
          0%   { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        @keyframes popIn {
          0%   { transform: scale(0); }
          100% { transform: scale(1); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .bell-swing { animation: bell-swing 1.6s ease-in-out infinite; }
        .notif-scroll::-webkit-scrollbar { width: 4px; }
        .notif-scroll::-webkit-scrollbar-track { background: transparent; }
        .notif-scroll::-webkit-scrollbar-thumb { background: #bcc0c4; border-radius: 10px; }
        .notif-panel { animation: slideIn .2s ease; }
        @media (max-width: 768px) {
          .notif-panel { width: 100vw !important; left: 0 !important; }
          .resize-handle { display: none !important; }
        }
      `}</style>

      {/* Hide everything if notice_allowed is false */}
      {noticeAllowed && (<>
      <button
        ref={iconRef}
        onMouseDown={handleIconMouseDown}
        onClick={() => { if (!isDraggingIcon) setPanelOpen(v => !v); }}
        style={{
          position: 'fixed', left: iconPosition.x, top: iconPosition.y,
          cursor: isDraggingIcon ? 'grabbing' : 'grab', zIndex: 9999,
          width: 52, height: 52, borderRadius: '50%',
          background: panelOpen
            ? 'linear-gradient(135deg, #1877f2, #0d5ed9)'
            : 'linear-gradient(135deg, #1877f2, #1a6ef5)',
          border: 'none',
          boxShadow: panelOpen
            ? '0 4px 20px rgba(24,119,242,0.55)'
            : '0 4px 16px rgba(24,119,242,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          userSelect: 'none', transition: 'all .25s',
        }}
      >
        {/* Ripple ring when unread */}
        {totalUnread > 0 && noticeAllowed && (
          <span style={{
            position: 'absolute', inset: -4, borderRadius: '50%',
            border: '2px solid rgba(24,119,242,0.4)',
            animation: 'ripple 1.8s ease-out infinite',
            pointerEvents: 'none',
          }} />
        )}
        <Bell
          size={24}
          color="#fff"
          className={totalUnread > 0 && noticeAllowed ? 'bell-swing' : ''}
          style={{ transformOrigin: '50% 2px', display: 'block' }}
        />

        {totalUnread > 0 && noticeAllowed && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            minWidth: 18, height: 18, borderRadius: 9,
            background: '#ff3b30', color: '#fff', fontSize: 10, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff', padding: '0 3px', pointerEvents: 'none',
            animation: 'popIn .3s cubic-bezier(.175,.885,.32,1.275)',
          }}>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {/* ── BACKDROP ── */}
      {panelOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          onClick={() => setPanelOpen(false)}
        />
      )}

      {/* ── PANEL ── */}
      {panelOpen && (
        <div
          ref={panelRef}
          className="notif-panel"
          style={{
            position: 'fixed', left: position.x, top: position.y,
            width, height: '100vh',
            background: '#fff',
            boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
            borderRadius: '0 0 0 12px',
            zIndex: 9999,
            display: 'flex', flexDirection: 'column',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
            cursor: isDragging ? 'move' : 'default',
          }}
        >
          {/* Resize Left */}
          <div
            className="resize-handle"
            style={{ position: 'absolute', left: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize', zIndex: 10 }}
            onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); setResizeSide('left'); setResizeStart({ x: e.clientX, width, posX: position.x }); }}
          >
            <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 14, height: 56, background: '#e4e6eb', borderRadius: '0 6px 6px 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GripVertical size={11} color="#65676b" />
            </div>
          </div>
          {/* Resize Right */}
          <div
            className="resize-handle"
            style={{ position: 'absolute', right: 0, top: 0, width: 6, height: '100%', cursor: 'ew-resize', zIndex: 10 }}
            onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); setResizeSide('right'); setResizeStart({ x: e.clientX, width, posX: position.x }); }}
          >
            <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: 14, height: 56, background: '#e4e6eb', borderRadius: '6px 0 0 6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <GripVertical size={11} color="#65676b" />
            </div>
          </div>

          {/* ── HEADER (FB style) ── */}
          <div
            onMouseDown={handleMouseDown}
            style={{ padding: '16px 16px 0', cursor: 'move', flexShrink: 0, background: '#fff' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#050505' }}>Notifications</h2>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: '#e4e6eb', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#050505',
                  }}
                >
                  <RefreshCw size={15} style={refreshing ? { animation: 'spin 1s linear infinite' } : {}} />
                </button>
                <button
                  onClick={() => setPanelOpen(false)}
                  title="Close"
                  style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: '#e4e6eb', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#050505',
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* FB-style tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
              {[
                { key: 'unread', label: 'All', count: notifications.length },
                { key: 'seen',   label: 'Read', count: seenNotifications.length },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 20,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 14,
                    background: activeTab === tab.key ? '#e7f0fd' : 'transparent',
                    color: activeTab === tab.key ? '#1877f2' : '#65676b',
                    display: 'flex', alignItems: 'center', gap: 5,
                    transition: 'all .15s',
                  }}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span style={{
                      fontSize: 11, fontWeight: 800,
                      background: activeTab === tab.key ? '#1877f2' : '#e4e6eb',
                      color: activeTab === tab.key ? '#fff' : '#65676b',
                      borderRadius: 10, padding: '1px 6px',
                    }}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#e4e6eb', marginTop: 8 }} />
          </div>

          {/* ── BODY ── */}
          <div className="notif-scroll" style={{ flex: 1, overflowY: 'auto', paddingTop: 8, background: '#fff' }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 12 }}>
                <Loader2 size={30} color="#1877f2" style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 14, color: '#65676b', fontWeight: 500 }}>Loading notifications…</span>
              </div>
            ) : !notifAllowed ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', gap: 10 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BellOff size={28} color="#65676b" />
                </div>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#050505' }}>Notifications off</p>
                <p style={{ margin: 0, fontSize: 13, color: '#65676b', textAlign: 'center', maxWidth: 240 }}>
                  Your account doesn't have notification access. Contact your admin.
                </p>
              </div>
            ) : displayList.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', gap: 10 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BellOff size={28} color="#65676b" />
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#050505' }}>
                  {activeTab === 'unread' ? 'All caught up!' : 'No read notifications'}
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#65676b', textAlign: 'center' }}>
                  {activeTab === 'unread' ? 'No new notifications at the moment.' : 'Notifications you ve opened will appear here.'}
                </p>
              </div>
            ) : (
              <>
                {activeTab === 'unread' && notifications.length > 0 && (
                  <p style={{ margin: '4px 16px 6px', fontSize: 13, fontWeight: 700, color: '#050505' }}>New</p>
                )}
                {displayList.map((notif) => (
                  <NotifRow
                    key={`${notif.type}-${notif.id}`}
                    notif={notif}
                    isFromSeen={activeTab === 'seen'}
                  />
                ))}
              </>
            )}

            {/* Hint bar */}
            {!loading && notifAllowed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '8px 16px 16px', padding: '8px 12px', background: '#f0f2f5', borderRadius: 8 }}>
                <AlertCircle size={12} color="#65676b" />
                <span style={{ fontSize: 11, color: '#65676b' }}>Click a notification to mark it as read and view details.</span>
              </div>
            )}
          </div>
        </div>
      )}

      </>)} {/* end noticeAllowed */}

      {/* ── MODAL ── */}
      {showModal && selectedNotification && (() => {
        const n  = selectedNotification;
        const tc = TYPE_CONFIG[n.type] || TYPE_CONFIG.IR;
        const ab = n.type === 'SA' ? getAdjTypeBadge(n.adjustmentType) : null;
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            onClick={closeModal}
          >
            <div
              style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e4e6eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: `linear-gradient(135deg, ${tc.color}22, ${tc.color}44)`, border: `2px solid ${tc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                      {tc.avatar}
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#65676b', fontWeight: 600 }}>{tc.label}</p>
                      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#050505' }}>{getNotifTitle(n)}</h2>
                    </div>
                  </div>
                  <button onClick={closeModal} style={{ width: 36, height: 36, borderRadius: '50%', background: '#e4e6eb', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={16} color="#050505" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '16px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: getStatusStyle(n.status, n.type).bg, color: getStatusStyle(n.status, n.type).color }}>
                    {getStatusText(n.status, n.type)}
                  </span>
                  {n.type === 'SA' && n.adjustmentType && ab && (
                    <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: ab.bg, color: ab.color, border: `1px solid ${ab.border}` }}>
                      {n.adjustmentType}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: '#65676b' }}>{formatDate(getNotifDate(n))}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {n.type === 'SA' && getSAModalFields(n).map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'GR' && getGRModalFields(n).map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'FR' && getFRModalFields(n).map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'RR' && [
                    ['Item Code', n.itemCode || 'N/A', false], ['Item Description', n.itemDescription || 'N/A', false],
                    ['Return Qty', n.requestedReturnQty ?? 'N/A', false], ['Return Reason', n.returnReason || 'N/A', false],
                    ['Warehouse', n.warehouseCode || 'N/A', false], ['Batch No', n.batchNo || 'N/A', false],
                    ['Requested By', n.requestedByName || 'N/A', false], ['MR Header ID', n.mrHeaderId || 'N/A', false],
                  ].map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'IR' && [
                    ['MR Number', n.mrNo, false], ['Purpose', n.purpose || 'N/A', false],
                    ['Department', n.departmentName || 'N/A', false], ['Location', n.locationName || 'N/A', false],
                    ['Requested By', n.requestedByName || 'N/A', false], ['Remarks', n.remarks || 'N/A', false],
                  ].map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'PR' && [
                    ['PR Number', n.prNo, false], ['PR Type', n.prType === 0 ? 'Regular' : n.prType === 1 ? 'Emergency' : `Type ${n.prType}`, false],
                    ['Company ID', n.companyId || 'N/A', false], ['Location ID', n.locationId || 'N/A', false],
                    ['Created By', `User ${n.createdBy}`, false], ['Approver Level', n.approverLevel, false],
                  ].map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                  {n.type === 'PO' && [
                    ['PO Number', n.poNumber, false], ['Vendor ID', n.vendorId, false],
                    ['Payment Terms', n.paymentTerms || 'N/A', false],
                    ['Delivery Date', n.deliveryDate ? new Date(n.deliveryDate).toLocaleDateString() : 'N/A', false],
                    ['Ship To Location', n.shipTo_Location || 'N/A', true],
                    ['Contact Person', n.shipTo_ContactPerson || 'N/A', false],
                    ['Receiving Status', n.receivingStatus || 'N/A', false],
                  ].map(([label, val, full]) => <FieldCard key={label} label={label} val={val} full={full} />)}
                </div>

                {n.type === 'SA' && (
                  <div style={{ marginTop: 12, background: 'linear-gradient(135deg, #eff6ff, #e0e7ff)', border: '1px solid #c7d2fe', borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Quantity Summary</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, textAlign: 'center', background: '#fff', borderRadius: 8, padding: '10px 8px', border: '1px solid #e2e8f0' }}>
                        <p style={{ margin: '0 0 2px', fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>BEFORE</p>
                        <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{n.quantityBefore ?? 0}</p>
                      </div>
                      <div style={{ textAlign: 'center', padding: '0 4px' }}>
                        <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: n.adjustmentQty >= 0 ? '#16a34a' : '#dc2626' }}>
                          {n.adjustmentQty >= 0 ? '+' : ''}{n.adjustmentQty ?? 0}
                        </p>
                        <p style={{ margin: 0, fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>ADJ</p>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', background: '#eff6ff', borderRadius: 8, padding: '10px 8px', border: '1px solid #bfdbfe' }}>
                        <p style={{ margin: '0 0 2px', fontSize: 10, color: '#1d4ed8', fontWeight: 600 }}>AFTER</p>
                        <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1d4ed8' }}>{n.quantityAfter ?? 0}</p>
                      </div>
                    </div>
                  </div>
                )}

                {n.type === 'GR' && n.grDetails && n.grDetails.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Items Received ({n.grDetails.length})</p>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 9, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: '#f0fdfa' }}>
                            {['Item Code', 'Description', 'Qty', 'UOM', 'Batch'].map(h => (
                              <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#0f766e', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {n.grDetails.map((d, i) => (
                            <tr key={i} style={{ borderBottom: i < n.grDetails.length - 1 ? '1px solid #f1f5f9' : 'none' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <td style={{ padding: '7px 10px', fontWeight: 600, color: '#0f172a' }}>{d.itemCode || 'N/A'}</td>
                              <td style={{ padding: '7px 10px', color: '#475569', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.itemDescription || 'N/A'}</td>
                              <td style={{ padding: '7px 10px', fontWeight: 700, color: '#0f766e' }}>{d.receivedQty ?? 0}</td>
                              <td style={{ padding: '7px 10px', color: '#64748b' }}>{d.uom || 'pcs'}</td>
                              <td style={{ padding: '7px 10px', color: '#64748b' }}>{d.batchNo || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {n.remarks && !['FR', 'GR'].includes(n.type) && (
                  <div style={{ marginTop: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 9, padding: '10px 13px' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: '#a16207', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Remarks</p>
                    <p style={{ margin: 0, fontSize: 13, color: '#0f172a' }}>{n.remarks}</p>
                  </div>
                )}
                {n.type === 'FR' && n.description && (
                  <div style={{ marginTop: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 9, padding: '10px 13px' }}>
                    <p style={{ margin: '0 0 4px', fontSize: 10, fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</p>
                    <p style={{ margin: 0, fontSize: 13, color: '#0f172a' }}>{n.description}</p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '12px 20px', borderTop: '1px solid #e4e6eb', background: '#f0f2f5' }}>
                <button
                  onClick={closeModal}
                  style={{ width: '100%', background: '#1877f2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#166fe5'}
                  onMouseLeave={e => e.currentTarget.style.background = '#1877f2'}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}